import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { GAEvents } from '../utils/analytics';
import '../styles/Navbar.css';
import '../styles/Footer.css';

const Navbar = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 🔥 NEW: Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }

    return () => {
      document.body.classList.remove('mobile-menu-open');
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const handleLogout = async (e) => {
    e.preventDefault();

    // ✅ TRACK LOGOUT
    GAEvents.logout(user?.id);

    // ✅ Close mobile menu first
    closeMobileMenu();

    try {
      // ✅ Wait for logout to complete
      await logout();

      // ✅ Force complete redirect (prevents race conditions)
      window.location.href = '/';
    } catch (err) {
      console.error('Logout error:', err);
      // ✅ Fallback: force redirect anyway
      window.location.href = '/';
    }
  };

  // ✅ TRACK LANGUAGE CHANGE
  const handleLanguageChange = (newLang) => {
    const fromLang = i18n.language;
    i18n.changeLanguage(newLang);
    GAEvents.languageChanged(fromLang, newLang);
  };

  return (
    <nav className={`navbar ${scrolled ? 'navbar-scrolled' : ''}`}>
      <div className="navbar-container">
        {/* Brand */}
        <div className="navbar-brand">
          <Link to="/" onClick={closeMobileMenu} aria-label="BetterPDP Home">
            <span className="logo-text">Better</span>
            <span className="logo-text-gradient">PDP</span>
          </Link>
        </div>

        {/* Links + Right section */}
        <div className={`navbar-links ${mobileMenuOpen ? 'mobile-active' : ''}`}>
          <Link to="/" className="navbar-link" onClick={closeMobileMenu}>
            {t('nav.home')}
          </Link>

          {/* 🔥 NEW: About link — visible to everyone */}
          <Link to="/about" className="navbar-link" onClick={closeMobileMenu}>
            {t('nav.about')}
          </Link>

          {isAuthenticated && (
            <Link to="/dashboard" className="navbar-link" onClick={closeMobileMenu}>
              {t('nav.dashboard')}
            </Link>
          )}

          {/* ✅ PRICING REMOVED FOR BETA */}

          {/* Right side: user + auth + language */}
          <div className="navbar-right">
            {isAuthenticated ? (
              <>
                <div className="navbar-user">
                  <div className="user-avatar">{user?.name?.charAt(0) || 'U'}</div>
                  <span className="user-name">{user?.name?.split(' ')[0] || 'User'}</span>
                </div>
                <button onClick={handleLogout} className="navbar-logout">
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="navbar-login" onClick={closeMobileMenu}>
                  {t('nav.login')}
                </Link>
                <Link to="/signup" className="navbar-signup" onClick={closeMobileMenu}>
                  {t('nav.signup')}
                </Link>
              </>
            )}

            {/* Language switcher */}
            <div className="language-switcher language-switcher--navbar">
              <button
                className={`lang-btn ${i18n.language?.startsWith('it') ? 'active' : ''}`}
                onClick={() => handleLanguageChange('it')}
                aria-label="Italiano"
              >
                🇮🇹 <span>IT</span>
              </button>
              <button
                className={`lang-btn ${i18n.language?.startsWith('en') ? 'active' : ''}`}
                onClick={() => handleLanguageChange('en')}
                aria-label="English"
              >
                🇬🇧 <span>EN</span>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Toggle */}
        <button
          className={`navbar-mobile-toggle ${mobileMenuOpen ? 'active' : ''}`}
          onClick={() => setMobileMenuOpen((s) => !s)}
          aria-label="Toggle menu"
        >
          <span></span><span></span><span></span>
        </button>
      </div>
    </nav>
  );
};

export default Navbar;